<template>
    <Dropdown ref="dropdown" v-model="isOpen" :distance="0">
        <template #trigger>
            <div class="tw-relative">
                <button
                    class="tw-border tw-border-gray-300 tw-rounded-md tw-py-2 tw-px-4 tw-flex tw-items-center tw-gap-3 tw-w-full tw-text-left tw-select-none hover:tw-border-gray-400"
                    :class="{
                        '!tw-border-gray-400 tw-bg-slate-50': isOpen,
                    }"
                >
                    <i
                        class="mdi tw-text-2xl"
                        :class="{
                            'mdi-receipt-text-remove-outline': !hasSelection,
                            'mdi-receipt-text': hasSelection,
                        }"
                    ></i>
                    <div class="tw-flex-1 tw-flex tw-items-center">
                        <template v-if="!hasSelection">
                            <div class="tw-flex-1 tw-flex tw-flex-col">
                                <span class="tw-text-sm tw-font-medium">No Purchase Order Selected</span>
                                <span class="tw-text-gray-400">Click to find and select a purchase order</span>
                            </div>
                        </template>
                        <template v-else-if="multiple && selectedPurchaseOrders.length > 1">
                            <div class="tw-flex-1 tw-flex tw-flex-col">
                                <span class="tw-text-sm tw-font-medium">
                                    {{ selectedPurchaseOrders.length }} Purchase Orders Selected.
                                    <span class="tw-text-gray-400">{{ committedString }}</span>
                                </span>
                                <span class="tw-text-gray-400">Click to manage selection</span>
                            </div>
                            <div class="tw-flex tw-flex-col tw-items-end">
                                <span class="tw-text-sm">
                                    {{
                                        formatCurrency(
                                            selectedTotalTarget,
                                            2,
                                            selectedPurchaseOrders[0].target_currency
                                        )
                                    }}
                                </span>
                                <span class="tw-text-xs tw-text-gray-400">
                                    Tax:
                                    {{ formatCurrency(selectedTotalTax, 2, selectedPurchaseOrders[0].target_currency) }}
                                </span>
                            </div>
                        </template>
                        <template v-else>
                            <div class="tw-flex-1 tw-flex tw-flex-col">
                                <div class="tw-flex tw-gap-1.5">
                                    <span class="tw-text-sm tw-font-medium">{{ displayPurchaseOrder.reference }}</span>
                                    <span class="tw-text-sm">{{ displayPurchaseOrder.title }}</span>
                                    <span class="tw-text-gray-400">{{ committedString }}</span>
                                </div>
                                <span class="tw-text-gray-400">
                                    {{
                                        displayPurchaseOrder.supplier
                                            ? displayPurchaseOrder.supplier.name
                                            : 'Invalid Supplier'
                                    }}
                                    <span class="tw-mx-[1px]">&bull;</span>
                                    {{ displayPurchaseOrder.event.name }}
                                </span>
                            </div>
                            <div class="tw-flex tw-flex-col tw-items-end">
                                <span class="tw-text-sm">
                                    {{
                                        formatCurrency(
                                            displayPurchaseOrder.total_target,
                                            2,
                                            displayPurchaseOrder.target_currency
                                        )
                                    }}
                                </span>
                                <span class="tw-text-xs tw-text-gray-400">
                                    Tax:
                                    {{
                                        formatCurrency(
                                            displayPurchaseOrder.tax_amount_target,
                                            2,
                                            displayPurchaseOrder.target_currency
                                        )
                                    }}
                                </span>
                            </div>
                        </template>
                    </div>
                    <i class="mdi mdi-chevron-down tw-text-2xl"></i>
                </button>
                <Loader v-if="isLoadingInitial" fill />
                <div
                    v-if="errorMessage"
                    class="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-red-100/90 tw-rounded-md tw-border tw-border-red-300 tw-cursor-default tw-flex tw-gap-4 tw-items-center tw-px-6 tw-text-red-700"
                >
                    <i class="mdi mdi-alert tw-text-2xl"></i>
                    <div class="tw-flex tw-flex-col tw-leading-4">
                        <span class="tw-font-medium">An Error Occurred</span>
                        <span>{{ errorMessage || 'No error message' }}</span>
                    </div>
                </div>
            </div>
        </template>

        <div class="tw-w-full">
            <div class="tw-flex tw-justify-left">
                <Tooltip position="right" label="Show Approved POs only" class="tw-ml-2">
                    <Checkbox v-model="selectApprovedOnly" class="tw-top-2 tw-left-2 tw-z-10">Approved</Checkbox>
                </Tooltip>
                <Tooltip position="right" label="Show uncommitted POs" class="tw-ml-2">
                    <Checkbox v-model="selectUncommitted" class="tw-top-2 tw-left-2 tw-z-10">Uncommitted</Checkbox>
                </Tooltip>
                <Tooltip position="right" label="Show closed POs" class="tw-ml-2">
                    <Checkbox v-model="selectClosed" class="tw-top-2 tw-left-2 tw-z-10">Closed</Checkbox>
                </Tooltip>
            </div>
            <div class="tw-p-2 tw-border-b">
                <Textbox v-model="searchQuery" placeholder="Search Purchase Orders..." class="tw-w-full" />
            </div>
            <div class="tw-relative">
                <template v-if="!searchResultsData.searchResults || !searchResultsData.searchResults.total">
                    <div class="tw-p-4 tw-text-center tw-text-gray-500">No purchase orders found.</div>
                </template>
                <template v-else>
                    <button
                        v-for="result in searchResultsData.data"
                        :key="result.id"
                        class="tw-w-full tw-text-left tw-px-3 tw-py-2 hover:tw-bg-gray-100 tw-flex tw-items-center tw-gap-3"
                        :class="{ 'tw-bg-blue-50': multiple && isSelected(result.id) }"
                        @click="selectPurchaseOrder(result)"
                    >
                        <i
                            v-if="multiple"
                            class="mdi tw-text-lg"
                            :class="
                                isSelected(result.id)
                                    ? 'mdi-checkbox-marked tw-text-blue-600'
                                    : 'mdi-checkbox-blank-outline tw-text-gray-400'
                            "
                        ></i>
                        <div class="tw-flex-1 tw-flex tw-items-center tw-gap-6">
                            <div class="tw-flex-1 tw-flex tw-flex-col">
                                <div class="tw-flex tw-gap-1.5">
                                    <span class="tw-text-sm tw-font-medium">{{ result.reference }}</span>
                                    <span class="tw-text-sm">{{ result.title }}</span>
                                </div>
                                <span class="tw-text-gray-400 tw-text-xs">
                                    {{ result.supplier ? result.supplier.name : 'Invalid Supplier' }}
                                    <span class="tw-mx-[1px]">&bull;</span>
                                    {{ result.event ? result.event.name : 'No Event' }}
                                </span>
                            </div>
                            <div class="tw-flex tw-flex-col tw-items-end">
                                <span class="tw-text-xs tw-text-gray-400">
                                    ({{ formatCurrency(committedValue(result), 2, result.target_currency) }})
                                </span>
                                <span class="tw-text-sm">
                                    {{ formatCurrency(result.total_target, 2, result.target_currency) }}
                                </span>
                                <span class="tw-text-xs tw-text-gray-400">
                                    Tax: {{ formatCurrency(result.tax_amount_target, 2, result.target_currency) }}
                                </span>
                            </div>
                        </div>
                    </button>
                    <!-- Pagination controls if required -->
                    <div
                        v-if="hasPaginator"
                        class="tw-w-full tw-text-left tw-px-3 tw-py-2 hover:tw-bg-gray-100 tw-flex tw-items-center tw-justify-end tw-gap-3"
                    >
                        <Paginator
                            :data="searchResultsData.searchResults"
                            :range-before="rangeBefore"
                            :range-after="rangeAfter"
                            @change-page="searchPurchaseOrders"
                        />
                    </div>
                </template>
                <Loader v-if="isLoading" fill />
            </div>
        </div>
    </Dropdown>
</template>
<script setup>
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { formatCurrency, route } from '@/js/utils.js';
import axios from 'axios';
import { debounce } from 'lodash';
import { useDataStore } from '@/js/stores/DataStore.js';
import Dropdown from '@/js/components/controls/Dropdown.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import Loader from '@/js/components/controls/Loader.vue';
import Checkbox from '@/js/components/controls/Checkbox.vue';
import Tooltip from '@/js/components/Tooltip.vue';
import Paginator from '@/js/components/controls/Paginator.vue';

const props = defineProps({
    endpointQueryParams: {
        type: Object,
        default: () => ({}),
    },
    accountId: {
        type: [Number, String],
        required: true,
    },
    eventId: {
        type: [Number, String],
        required: false,
    },
    multiple: {
        type: Boolean,
        default: false,
    },
});

onMounted(() => {
    selectedPurchaseOrders.value = [];
    getSelectedPurchaseOrders();
});

const model = defineModel({
    type: Array,
    default: () => [],
});

const emit = defineEmits(['updated']);

const dropdown = useTemplateRef('dropdown');
const isLoadingInitial = ref(false);
const isLoading = ref(false);
const isOpen = ref(false);
const selectedPurchaseOrders = ref([]);
const searchQuery = ref(null);
const errorMessage = ref(null);
const store = useDataStore();
const currentEventId = props.eventId ? props.eventId : store.get('current_event.id');
const selectApprovedOnly = ref(true);
const selectUncommitted = ref(false);
const selectClosed = ref(false);

const searchResultsData = ref({
    searchResults: [],
    data: [],
    total: 0,
    page: 0,
    currentPage: 0,
});

const perPage = 7;
const rangeBefore = 2;
const rangeAfter = 2;

const committedString = computed(() => {
    const committedTotal = selectedPurchaseOrders.value.reduce((sum, po) => sum + Number(po.total_invoiced || 0), 0);
    const formattedValue = formatCurrency(committedTotal, 2, selectedPurchaseOrders.value[0]?.target_currency || 'GBP');
    return `(committed: ${formattedValue})`;
});

const hasSelection = computed(() => selectedPurchaseOrders.value.length > 0);
const hasPaginator = computed(() => searchResultsData.value.total > perPage);
const displayPurchaseOrder = computed(() => selectedPurchaseOrders.value[0] || null);
const selectedTotalTarget = computed(() =>
    selectedPurchaseOrders.value.reduce((sum, po) => sum + Number(po.total_target || 0), 0)
);
const selectedTotalTax = computed(() =>
    selectedPurchaseOrders.value.reduce((sum, po) => sum + Number(po.tax_amount_target || 0), 0)
);

function isSelected(id) {
    return model.value.includes(id);
}

const searchEndpoint = computed(() => {
    if (currentEventId) {
        // If an event ID is provided, use the event-specific endpoint
        return route('api.account.event.purchaseorders', {
            account: props.accountId,
            event: currentEventId,
        });
    }
    // If no event ID is provided, use the account-specific endpoint
    return route('api.account.purchase-orders', {
        account: props.accountId,
    });
});

async function getSelectedPurchaseOrders() {
    if (!model.value || !model.value.length) {
        selectedPurchaseOrders.value = [];
        return;
    }
    isLoadingInitial.value = true;
    try {
        const requests = model.value.map((id) =>
            axios.get(
                route('api.account.purchase-orders.get', {
                    account: props.accountId,
                    purchaseOrder: id,
                })
            )
        );
        const responses = await Promise.all(requests);
        selectedPurchaseOrders.value = responses.map((r) => r.data);
        // if the selectedPurchaseOrders is empty, emit an updated event with isHydrating true
        if (selectedPurchaseOrders.value) {
            emit('updated', selectedPurchaseOrders.value);
        } else {
            emit('updated', selectedPurchaseOrders.value, {
                isHydrating: true,
            });
        }
    } catch (error) {
        errorMessage.value =
            error.response?.data?.message ||
            'We were unable to fetch the selected purchase orders. Please try again later.';
    } finally {
        isLoadingInitial.value = false;
    }
}

async function searchPurchaseOrders(newPage = null) {
    isLoading.value = true;
    try {
        if (!newPage) {
            newPage = searchResultsData.value.page;
        }

        const url = new URL(searchEndpoint.value);
        url.searchParams.append('perPage', perPage);
        url.searchParams.append('page', newPage ?? searchResultsData.value.currentPage);
        if (searchQuery.value) {
            url.searchParams.append('query', searchQuery.value);
        }
        url.searchParams.append('approved', selectApprovedOnly.value ? 1 : 0);
        url.searchParams.append('closed', selectClosed.value ? 1 : 0);
        url.searchParams.append('uncommitted', selectUncommitted.value ? 1 : 0);
        const { data } = await axios.get(url.toString());

        searchResultsData.value = {
            searchResults: data,
            data: data.data,
            total: data.total,
            page: data.current_page,
            currentPage: data.current_page,
        };
    } catch (error) {
        errorMessage.value =
            error.response?.data?.message || 'We were unable to search for purchase orders. Please try again later.';
    } finally {
        isLoading.value = false;
    }
}

function selectPurchaseOrder(purchaseOrder) {
    if (props.multiple) {
        const index = model.value.indexOf(purchaseOrder.id);
        if (index === -1) {
            model.value = [...model.value, purchaseOrder.id];
            selectedPurchaseOrders.value = [...selectedPurchaseOrders.value, purchaseOrder];
        } else {
            model.value = model.value.filter((id) => id !== purchaseOrder.id);
            selectedPurchaseOrders.value = selectedPurchaseOrders.value.filter((po) => po.id !== purchaseOrder.id);
        }
        emit('updated', selectedPurchaseOrders.value);
    } else {
        dropdown.value?.close();
        model.value = [purchaseOrder.id];
        selectedPurchaseOrders.value = [purchaseOrder];
        isOpen.value = false;
        searchQuery.value = null;
        searchResultsData.value.searchResults = null;
        emit('updated', [purchaseOrder]);
    }
}

const debouncedSearch = debounce(searchPurchaseOrders, 300);

const committedValue = (purchaseOrder) => {
    return purchaseOrder.total_invoiced;
};

watch(isOpen, (newVal) => {
    if (!newVal) return;
    searchPurchaseOrders();
});

watch(searchQuery, () => {
    debouncedSearch();
});

watch(displayPurchaseOrder, () => {
    if (!displayPurchaseOrder.value) return;

    emit('updated', [displayPurchaseOrder.value]);
});

watch(model, () => {
    getSelectedPurchaseOrders();
});
watch([selectApprovedOnly, selectUncommitted, selectClosed], () => {
    searchResultsData.value.currentPage = 1;
    searchPurchaseOrders(1);
});

defineExpose({
    selectedPurchaseOrders,
});
</script>
